import { useEffect, useRef, useState } from "react";
import { EmailResponse } from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { EmailAdminController } from "api/main/EmailAdminController";
import { useTestMode } from "context/TestModeContext";
import { mockEmails } from "data/mockData";
import { adminTheme, statusColors, getStatusLabel } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Divider,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";

function getStatusColor(status: string) {
  switch (status) {
    case "SUCCEED":
      return statusColors.succeed;
    case "FAILED":
      return statusColors.failed;
    case "PREPARED":
      return statusColors.prepared;
    default:
      return statusColors.pending;
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case "SUCCEED":
      return <CheckCircleIcon sx={{ fontSize: 18 }} />;
    case "FAILED":
      return <ErrorIcon sx={{ fontSize: 18 }} />;
    default:
      return <ScheduleIcon sx={{ fontSize: 18 }} />;
  }
}

export default function EmailPage() {
  const emailApi = useRef(createApi("email") as EmailAdminController);
  const { isTestMode } = useTestMode();
  const [emails, setEmails] = useState<EmailResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailResponse | null>(null);

  const handleOpenPreview = (email: EmailResponse) => {
    setSelectedEmail(email);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedEmail(null);
  };

  useEffect(() => {
    if (isTestMode) {
      setEmails(mockEmails);
      setLoading(false);
    } else {
      handleDataFetch();
    }
  }, [isTestMode]);

  async function handleDataFetch() {
    setLoading(true);
    try {
      const data = await emailApi.current.findAll({});
      setEmails(data || []);
    } catch (error) {
      console.error("Error fetching emails:", error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: adminTheme.colors.textPrimary,
              }}
            >
              Laiškai
            </Typography>
            <Typography
              variant="body"
              sx={{ color: adminTheme.colors.textSecondary }}
            >
              Peržiūrėkite išsiųstų laiškų istoriją
            </Typography>
          </Box>
          <Tooltip title="Atnaujinti">
            <IconButton
              onClick={() => !isTestMode && handleDataFetch()}
              sx={{
                bgcolor: adminTheme.colors.background,
                border: `1px solid ${adminTheme.colors.border}`,
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Iš viso laiškų
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}
            >
              {emails.length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Išsiųsti
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.success }}
            >
              {emails.filter((e) => e.status === "SUCCEED").length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Nepavyke
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.error }}
            >
              {emails.filter((e) => e.status === "FAILED").length}
            </Typography>
          </Card>
        </Box>

        {/* Table */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: adminTheme.colors.backgroundAlt,
                    "& th": {
                      fontWeight: 600,
                      color: adminTheme.colors.textPrimary,
                      borderBottom: `2px solid ${adminTheme.colors.border}`,
                      py: 2,
                    },
                  }}
                >
                  <TableCell>Tema</TableCell>
                  <TableCell>Gavėjo el. paštas</TableCell>
                  <TableCell>Tipas</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Statusas</TableCell>
                  <TableCell align="right">Veiksmai</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emails.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: adminTheme.colors.textMuted,
                        }}
                      >
                        <EmailIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography>Nėra laiškų</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  emails.map((email) => (
                    <TableRow
                      key={email.id}
                      onClick={() => handleOpenPreview(email)}
                      sx={{
                        cursor: "pointer",
                        transition: adminTheme.transitions.fast,
                        "&:hover": {
                          bgcolor: "rgba(99, 102, 241, 0.04)",
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              bgcolor: getStatusColor(email.status || "").bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: getStatusColor(email.status || "").text,
                            }}
                          >
                            {getStatusIcon(email.status || "")}
                          </Box>
                          <Typography sx={{ fontWeight: 500 }}>
                            {email.subject || "Be temos"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{email.emailTo || "-"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={email.type || "N/A"}
                          size="small"
                          sx={{
                            bgcolor: adminTheme.colors.backgroundAlt,
                            color: adminTheme.colors.textSecondary,
                            fontWeight: 500,
                            fontSize: "0.7rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                          {email.dateSent
                            ? new Date(email.dateSent).toLocaleString("lt-LT")
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(email.status)}
                          size="small"
                          sx={{
                            bgcolor: getStatusColor(email.status || "").bg,
                            color: getStatusColor(email.status || "").text,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Peržiūrėti">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenPreview(email);
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Email Preview Dialog */}
        <Dialog
          open={previewOpen}
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: adminTheme.borderRadius.lg,
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: adminTheme.colors.backgroundAlt,
              borderBottom: `1px solid ${adminTheme.colors.border}`,
              py: 2,
              px: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: adminTheme.borderRadius.md,
                  background: adminTheme.gradients.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <EmailIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}>
                  Laiško peržiūra
                </Typography>
                <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                  {selectedEmail?.type || "N/A"}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {selectedEmail && (
              <>
                {/* Email Header Info */}
                <Box sx={{ p: 3, bgcolor: adminTheme.colors.background, borderBottom: `1px solid ${adminTheme.colors.border}` }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: `${adminTheme.colors.primary}15`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: adminTheme.colors.primary,
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                          Gavėjas
                        </Typography>
                        <Typography sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                          {selectedEmail.emailTo || "N/A"}
                        </Typography>
                      </Box>
                      <Chip
                        label={getStatusLabel(selectedEmail.status)}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(selectedEmail.status || "").bg,
                          color: getStatusColor(selectedEmail.status || "").text,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Divider />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: `${adminTheme.colors.success}15`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: adminTheme.colors.success,
                        }}
                      >
                        <SendIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                          Tema
                        </Typography>
                        <Typography sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                          {selectedEmail.subject || "Be temos"}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                        {selectedEmail.dateSent
                          ? new Date(selectedEmail.dateSent).toLocaleString("lt-LT")
                          : "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Email Body Preview */}
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted, mb: 2, fontWeight: 600 }}>
                    Laiško turinys
                  </Typography>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: adminTheme.borderRadius.md,
                      border: `1px solid ${adminTheme.colors.border}`,
                      boxShadow: "none",
                      bgcolor: adminTheme.colors.backgroundAlt,
                      maxHeight: 400,
                      overflow: "auto",
                    }}
                  >
                    {selectedEmail.body ? (
                      <Box
                        dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
                        sx={{
                          "& *": {
                            maxWidth: "100%",
                          },
                          "& img": {
                            maxWidth: "100%",
                            height: "auto",
                          },
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: adminTheme.colors.textMuted, fontStyle: "italic" }}>
                        Laiško turinys neprieinamas
                      </Typography>
                    )}
                  </Card>
                </Box>

                {/* Dialog Actions */}
                <Box sx={{ p: 3, pt: 0, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={handleClosePreview}
                    sx={{
                      background: adminTheme.gradients.primary,
                      "&:hover": { background: adminTheme.gradients.primaryHover },
                      px: 4,
                    }}
                  >
                    Uždaryti
                  </Button>
                </Box>
              </>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
}
